import React, { useState, useEffect, useMemo } from 'react';

// Threshold helpers — centralised so colors line up with the bar fills below.
function levelFromPercent(value, type) {
  if (value == null || Number.isNaN(value)) return 'unknown';
  if (type === 'memory') {
    if (value >= 90) return 'critical';
    if (value >= 70) return 'warning';
    return 'good';
  }
  // CPU + generic
  if (value >= 85) return 'critical';
  if (value >= 60) return 'warning';
  return 'good';
}

function statusToLevel(status) {
  if (!status) return 'unknown';
  const s = String(status).toUpperCase();
  if (['RUNNING', 'UP', 'ACTIVE', 'CONNECTED', 'ONLINE'].includes(s)) return 'good';
  if (['LOADING', 'DEGRADED', 'STARTING', 'PENDING'].includes(s)) return 'warning';
  if (['ERROR', 'DOWN', 'OFFLINE', 'STOPPED', 'NOT_FOUND'].includes(s)) return 'critical';
  return 'unknown';
}

function StatusPip({ level, label }) {
  return (
    <span className={`opo-pip opo-pip-${level}`} title={label || level}>
      <span className="opo-pip-core" />
    </span>
  );
}

function MetricBar({ value, max = 100, level, label, suffix = '%', mono = true }) {
  const v = Number.isFinite(value) ? Math.max(0, Math.min(max, value)) : 0;
  const pct = (v / max) * 100;
  return (
    <div className="opo-metric">
      <div className="opo-metric-label">
        <span>{label}</span>
        <span className={`opo-metric-value opo-level-${level} ${mono ? 'opo-mono-value' : ''}`}>
          {value == null ? '—' : `${Math.round(v)}${suffix}`}
        </span>
      </div>
      <div className="opo-bar-track">
        <div
          className={`opo-bar-fill opo-fill-${level}`}
          style={{ width: `${pct}%` }}
        />
        <div className="opo-bar-grid" aria-hidden="true" />
      </div>
    </div>
  );
}

function StackedBar({ segments, total }) {
  const sum = total || segments.reduce((s, x) => s + (x.value || 0), 0) || 1;
  return (
    <div className="opo-stacked">
      <div className="opo-stacked-track">
        {segments.map((seg) => {
          const w = ((seg.value || 0) / sum) * 100;
          if (!w) return null;
          return (
            <div
              key={seg.id}
              className="opo-stacked-segment"
              style={{ width: `${w}%`, background: seg.color }}
              title={`${seg.label}: ${seg.value}`}
            />
          );
        })}
      </div>
      <div className="opo-stacked-legend">
        {segments.map((seg) => (
          <span key={seg.id} className="opo-stacked-legend-item">
            <span className="opo-legend-dot" style={{ background: seg.color }} />
            {seg.label}: <strong>{seg.value ?? 0}</strong>
          </span>
        ))}
      </div>
    </div>
  );
}

const OPOOverviewTab = () => {
  const [health, setHealth] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [activity, setActivity] = useState(null);
  const [agents, setAgents] = useState(null);
  const [records, setRecords] = useState({ invoices: [], compliance: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tickRefresh, setTickRefresh] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      try {
        setError(null);
        const [healthRes, metricsRes, actRes, agentsRes, invoicesRes, complianceRes] = await Promise.all([
          fetch('/api/opo/system-health'),
          fetch('/api/opticonnector/metrics'),
          fetch('/api/opo/activity?days=30&limit=1&offset=0'),
          fetch('/api/opo/agents'),
          fetch('/api/opo/auditor/invoices'),
          fetch('/api/opo/auditor/compliance-scan'),
        ]);
        if (cancelled) return;
        if (healthRes.ok)  setHealth(await healthRes.json());
        if (metricsRes.ok) setMetrics(await metricsRes.json());
        if (actRes.ok)     setActivity(await actRes.json());
        if (agentsRes.ok)  setAgents(await agentsRes.json());
        const invData = invoicesRes.ok ? await invoicesRes.json() : { invoices: [] };
        const compData = complianceRes.ok ? await complianceRes.json() : null;
        setRecords({
          invoices: Array.isArray(invData.invoices) ? invData.invoices : [],
          compliance: compData?.summary || null,
        });
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [tickRefresh]);

  const hostStats     = health?.hostStats || {};
  const pm2           = health?.pm2 || {};
  const serviceHealth = health?.serviceHealth || [];
  const dbStatus      = health?.dbStatus || {};
  const legalQueries  = metrics?.legal_assistant?.queriesHandled ?? 0;
  const normalQueries = metrics?.opo_assistant?.queriesHandled ?? 0;
  const totalQueries  = legalQueries + normalQueries;
  const conv24h       = activity?.summary?.total24h ?? 0;
  const conv7d        = activity?.summary?.total7d ?? 0;
  const conv30d       = activity?.summary?.total30d ?? 0;
  const ollamaCount   = agents?.ollama?.models?.length ?? 0;
  const issuedInvoices = records.invoices.filter((inv) => (inv.status || 'issued') === 'issued');
  const contractStatements = records.invoices.filter(
    (inv) => inv.document_type === 'contract_statement' || inv.include_in_revenue_total
  );
  const monthlyInvoices = records.invoices.filter(
    (inv) => inv.document_type === 'monthly_invoice' || inv.master_invoice
  );
  const compliancePct = records.compliance?.compliancePercent;

  const cpuLevel    = levelFromPercent(hostStats.cpuUsage, 'cpu');
  const memLevel    = levelFromPercent(hostStats.memoryPercentage, 'memory');
  const backendLvl  = statusToLevel(pm2.backendStatus === 'online' ? 'RUNNING' : pm2.backendStatus);
  const sqliteLvl   = statusToLevel(dbStatus.sqlite?.status);
  const pgLvl       = statusToLevel(dbStatus.postgres?.status);

  const servicesRunning = serviceHealth.filter((s) => statusToLevel(s.status) === 'good').length;
  const servicesTotal   = serviceHealth.length;
  const servicesPct     = servicesTotal ? (servicesRunning / servicesTotal) * 100 : 0;

  const aggregate = useMemo(() => {
    const levels = [cpuLevel, memLevel, backendLvl, sqliteLvl, pgLvl, ...serviceHealth.map((s) => statusToLevel(s.status))];
    if (levels.includes('critical')) return { level: 'critical', label: 'CRITICAL' };
    if (levels.includes('warning'))  return { level: 'warning',  label: 'DEGRADED' };
    if (levels.every((l) => l === 'good')) return { level: 'good', label: 'OPERATIONAL' };
    return { level: 'unknown', label: 'INITIALISING' };
  }, [cpuLevel, memLevel, backendLvl, sqliteLvl, pgLvl, serviceHealth]);

  if (loading && !health) {
    return (
      <div className="opo-loading-container">
        <div className="opo-loading-spinner" />
        <p>Calibrating Operator telemetry...</p>
      </div>
    );
  }

  if (error && !health) {
    return <div className="opo-error">Telemetry uplink lost: {error}</div>;
  }

  return (
    <div className="opo-overview opo-tactical">
      {/* ─── Hero status banner ─────────────────────────────────────────── */}
      <div className={`opo-hero opo-hero-${aggregate.level}`}>
        <div className="opo-hero-scan" aria-hidden="true" />
        <div className="opo-hero-left">
          <div className="opo-hero-eyebrow">OPERATOR STATUS</div>
          <div className="opo-hero-title">
            <span className={`opo-hero-pulse opo-pulse-${aggregate.level}`} />
            <h2>{aggregate.label}</h2>
          </div>
          <div className="opo-hero-sub">
            {servicesRunning}/{servicesTotal} services live
            {' · '}{ollamaCount} local LLMs loaded
            {' · '}{conv24h} OPO conversations in last 24h
          </div>
        </div>
        <div className="opo-hero-right">
          <button
            type="button"
            className="opo-refresh-btn"
            onClick={() => setTickRefresh((n) => n + 1)}
            title="Refresh telemetry"
          >
            <span className="opo-refresh-icon" /> SYNC
          </button>
          <div className="opo-hero-timestamp">
            {health?.lastUpdate ? new Date(health.lastUpdate).toLocaleTimeString() : '—'}
          </div>
        </div>
      </div>

      {/* ─── KPI strip ───────────────────────────────────────────────────── */}
      <div className="opo-kpi-row">
        <div className="opo-kpi">
          <div className="opo-kpi-label">CONVERSATIONS · 24H</div>
          <div className="opo-kpi-value opo-mono-value">{conv24h.toLocaleString()}</div>
          <div className="opo-kpi-foot">7d: {conv7d.toLocaleString()} · 30d: {conv30d.toLocaleString()}</div>
        </div>
        <div className="opo-kpi">
          <div className="opo-kpi-label">SERVICES ONLINE</div>
          <div className={`opo-kpi-value opo-mono-value opo-level-${aggregate.level}`}>
            {servicesRunning}<span className="opo-kpi-sep">/</span>{servicesTotal}
          </div>
          <div className="opo-bar-track opo-bar-mini">
            <div className={`opo-bar-fill opo-fill-${aggregate.level}`} style={{ width: `${servicesPct}%` }} />
          </div>
        </div>
        <div className="opo-kpi">
          <div className="opo-kpi-label">LOCAL LLMs LOADED</div>
          <div className="opo-kpi-value opo-mono-value">{ollamaCount}</div>
          <div className="opo-kpi-foot">
            Ollama: <span className={`opo-level-${statusToLevel(agents?.ollama?.status === 'connected' ? 'RUNNING' : 'DOWN')}`}>
              {agents?.ollama?.status || '—'}
            </span>
          </div>
        </div>
        <div className="opo-kpi">
          <div className="opo-kpi-label">HOST UPTIME</div>
          <div className="opo-kpi-value opo-mono-value">{hostStats.uptimeFormatted ?? '—'}</div>
          <div className="opo-kpi-foot">backend: {pm2.backendUptime != null
            ? `${Math.floor(pm2.backendUptime / 86400)}d ${Math.floor((pm2.backendUptime % 86400) / 3600)}h`
            : '—'}</div>
        </div>
      </div>

      {/* ─── Corporate records (documents & customer invoicing) ─────────── */}
      <div className="opo-section">
        <div className="opo-section-header">
          <h2>Corporate records</h2>
          <div className="opo-section-meta">
            <span className="opo-section-counter opo-level-good">
              {contractStatements.length} contract statement{contractStatements.length === 1 ? '' : 's'}
              {monthlyInvoices.length ? ` · ${monthlyInvoices.length} monthly invoices` : ''}
              {compliancePct != null ? ` · ${compliancePct}% compliance on file` : ''}
            </span>
          </div>
        </div>
        <p className="opo-section-desc">
          Financial workbooks, compliance library, and customer commercial invoices live under the Auditor agent — not on this telemetry view.
          Use the links below or open <strong>AI Agents → Auditor</strong> (now at <code>/opo/auditor</code>) for uploads and the full document checklist.
        </p>
        <div className="opo-records-grid">
          <a href="/opo/document/1?tab=INVOICING" className="opo-stat-card opo-card-tactical opo-record-link">
            <div className="opo-stat-header">
              <span className="opo-service-name">🧾 Commercial invoicing</span>
              <span className="opo-status-tag opo-tag-good">ISSUED</span>
            </div>
            <p className="opo-record-desc">
              Customer invoices (Optimum Realty · 12 × CAD 437.50 incl. GST). Contract statement OP-2026-0001 plus monthly tax invoices.
            </p>
            <div className="opo-detail-row">
              <span>On file</span>
              <span className="opo-mono-value">{monthlyInvoices.length || issuedInvoices.length}</span>
            </div>
          </a>
          <a href="/opo/document/1" className="opo-stat-card opo-card-tactical opo-record-link">
            <div className="opo-stat-header">
              <span className="opo-service-name">📊 ASPE preparation record</span>
              <span className="opo-status-tag opo-tag-good">DOC 1</span>
            </div>
            <p className="opo-record-desc">
              Company cost register, balance sheet, cash flow, bank position, SR&amp;ED, and related working tabs.
            </p>
            <div className="opo-detail-row">
              <span>Workbook</span>
              <span className="opo-mono-value">COMPANY_COST_REGISTER</span>
            </div>
          </a>
          <a href="/opo/auditor" className="opo-stat-card opo-card-tactical opo-record-link">
            <div className="opo-stat-header">
              <span className="opo-service-name">📁 Auditor document library</span>
              <span className="opo-status-tag opo-tag-good">OPEN</span>
            </div>
            <p className="opo-record-desc">
              Compliance checklist, vendor receipts, bank statements, AGM/T2 filings, and linked uploads.
            </p>
            <div className="opo-detail-row">
              <span>Compliance</span>
              <span className="opo-mono-value">
                {compliancePct != null ? `${compliancePct}% on file` : '—'}
              </span>
            </div>
          </a>
        </div>
      </div>

      {/* ─── Telemetry grid ─────────────────────────────────────────────── */}
      <div className="opo-stats-grid">
        <div className="opo-stat-card opo-card-tactical">
          <div className="opo-stat-header">
            <h3>OPO System Status</h3>
            <span className={`opo-status-tag opo-tag-${backendLvl}`}>
              <StatusPip level={backendLvl} />
              {pm2.backendStatus === 'online' ? 'RUNNING' : (pm2.backendStatus || '—')}
            </span>
          </div>
          <div className="opo-stat-details">
            <div className="opo-detail-row"><span>Hostname</span><span className="opo-mono-value">{health?.hostname ?? '—'}</span></div>
            <div className="opo-detail-row"><span>Internal IP</span><span className="opo-mono-value">{health?.hostIp ?? '—'}</span></div>
            <div className="opo-detail-row"><span>Backend uptime</span>
              <span className="opo-mono-value">
                {pm2.backendUptime != null
                  ? `${Math.floor(pm2.backendUptime / 86400)}d ${Math.floor((pm2.backendUptime % 86400) / 3600)}h ${Math.floor((pm2.backendUptime % 3600) / 60)}m`
                  : '—'}
              </span>
            </div>
          </div>
        </div>

        <div className="opo-stat-card opo-card-tactical">
          <div className="opo-stat-header">
            <h3>Performance</h3>
            <span className={`opo-status-tag opo-tag-${cpuLevel === 'critical' || memLevel === 'critical' ? 'critical' : (cpuLevel === 'warning' || memLevel === 'warning' ? 'warning' : 'good')}`}>
              <StatusPip level={cpuLevel === 'critical' || memLevel === 'critical' ? 'critical' : (cpuLevel === 'warning' || memLevel === 'warning' ? 'warning' : 'good')} />
              LIVE
            </span>
          </div>
          <div className="opo-stat-details opo-stat-bars">
            <MetricBar value={hostStats.cpuUsage}        level={cpuLevel} label="CPU" />
            <MetricBar value={hostStats.memoryPercentage} level={memLevel} label="Memory" />
          </div>
        </div>

        <div className="opo-stat-card opo-card-tactical">
          <div className="opo-stat-header">
            <h3>Query distribution · 30d</h3>
            <span className="opo-status-tag opo-tag-good"><StatusPip level="good" />{totalQueries.toLocaleString()} TOTAL</span>
          </div>
          <div className="opo-stat-details">
            <StackedBar
              total={totalQueries}
              segments={[
                { id: 'legal',  label: 'Legal',  value: legalQueries,  color: '#3cfcf9' },
                { id: 'normal', label: 'Normal', value: normalQueries, color: '#a78bfa' },
              ]}
            />
            {totalQueries === 0 && (
              <div className="opo-empty-hint">
                No queries logged in last 30 days · OPO assistants idle.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ─── Service health ────────────────────────────────────────────── */}
      <div className="opo-section">
        <div className="opo-section-header">
          <h2>Service health</h2>
          <div className="opo-section-meta">
            <span className={`opo-section-counter opo-level-${aggregate.level}`}>
              {servicesRunning}/{servicesTotal} ONLINE
            </span>
            <div className="opo-bar-track opo-bar-mini opo-bar-section">
              <div className={`opo-bar-fill opo-fill-${aggregate.level}`} style={{ width: `${servicesPct}%` }} />
            </div>
          </div>
        </div>
        <div className="opo-services-grid">
          {serviceHealth.map((svc) => {
            const lvl = statusToLevel(svc.status);
            return (
              <div key={svc.id} className={`opo-stat-card opo-service-card opo-card-tactical opo-card-${lvl}`}>
                <div className="opo-stat-header">
                  <span className="opo-service-name"><StatusPip level={lvl} />{svc.name}</span>
                  <span className={`opo-status-tag opo-tag-${lvl}`}>{svc.status}</span>
                </div>
                {svc.port && (
                  <div className="opo-detail-row">
                    <span>Port</span><span className="opo-mono-value">:{svc.port}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* ─── Database connections ──────────────────────────────────────── */}
      <div className="opo-section">
        <div className="opo-section-header">
          <h2>Database connections</h2>
          <div className="opo-section-meta">
            <span className={`opo-section-counter opo-level-${sqliteLvl === 'good' && pgLvl === 'good' ? 'good' : (sqliteLvl === 'critical' || pgLvl === 'critical' ? 'critical' : 'warning')}`}>
              {[sqliteLvl, pgLvl].filter((l) => l === 'good').length}/2 LINKED
            </span>
          </div>
        </div>
        <div className="opo-db-grid">
          <div className={`opo-stat-card opo-card-tactical opo-card-${sqliteLvl}`}>
            <div className="opo-stat-header">
              <span className="opo-service-name"><StatusPip level={sqliteLvl} />SQLite · optiprime.db</span>
              <span className={`opo-status-tag opo-tag-${sqliteLvl}`}>{dbStatus.sqlite?.status ?? '—'}</span>
            </div>
            <div className="opo-detail-row"><span>Driver</span><span className="opo-mono-value">sqlite3</span></div>
          </div>
          <div className={`opo-stat-card opo-card-tactical opo-card-${pgLvl}`}>
            <div className="opo-stat-header">
              <span className="opo-service-name"><StatusPip level={pgLvl} />PostgreSQL · optiprime_main</span>
              <span className={`opo-status-tag opo-tag-${pgLvl}`}>{dbStatus.postgres?.status ?? '—'}</span>
            </div>
            <div className="opo-detail-row"><span>Driver</span><span className="opo-mono-value">node-postgres</span></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OPOOverviewTab;
